import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { authFetch } from "../../utils/authFetch";
import { BASE_URL } from "../../utils/config";
import { useAuth } from "../../context/AuthContext";
import { useFlow } from "../../context/FlowContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const OTPVerification = ({ setHideFooter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    isAuthenticated,
    currentUser,
    login,
    logout: authLogout,
    fetchAndUpdateUser,
  } = useAuth();
  const { setFlow } = useFlow();

  const [isLoading, setIsLoading] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [verificationStep, setVerificationStep] = useState(
    searchParams.get("from") === "register" ? "register" : (searchParams.get("from") === "login" ? false : false)
  ); // 'register' means show verify OTP, false means show send OTP
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showUpdatePhone, setShowUpdatePhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");

  // Get user data from URL params or localStorage
  const emailFromParams = searchParams.get("email");
  const phoneFromParams = searchParams.get("phone");
  let isFromLogin = searchParams.get("from") === "login";

  console.log(
    "from",
    emailFromParams,
    phoneFromParams,
    isFromLogin,
    verificationStep
  );

  useEffect(() => {
    setHideFooter(true);
    return () => {
      // Polling disabled to prevent property changes during animation
      setHideFooter(false);
    };
  }, [setHideFooter]);

  // Timer effect for OTP expiry
  useEffect(() => {
    if (!otpExpiry) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const expiryTime = new Date(otpExpiry).getTime();
      const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));

      setTimeRemaining(remaining);

      if (remaining <= 0) {
        setOtpExpiry(null);
        setTimeRemaining(0);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [otpExpiry]);

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 6) {
      setOtpCode(value);
    }
  };

  // const handleAutoSendOTPForRegistration = async () => {
  //   try {
  //     // Get user data from localStorage (set during registration)
  //     const userData = JSON.parse(localStorage.getItem("user") || "{}");
  //     const userEmail = userData.email || "";
  //     const userPhone = userData.mobile || "";

  //     if (userEmail || userPhone) {
  //       setUserEmail(userEmail);
  //       setUserPhone(userPhone);
  //       await handleSendOTP(userEmail, userPhone);
  //     } else {
  //       // If no contact info available, show contact form
  //       setVerificationStep("contact");
  //     }
  //   } catch (error) {
  //     console.error("Error auto-sending OTP for registration:", error);
  //     // Fallback to contact form
  //     setVerificationStep("contact");
  //   }
  // };

  useEffect(() => {
    // Set user contact info
    if (emailFromParams) setUserEmail(emailFromParams);
    if (phoneFromParams) setUserPhone(phoneFromParams);

    // If coming from registration, auto-send OTP and show verify screen
    // This ensures "Send OTP" screen is hidden and "Verify OTP" is shown directly
    if (!isFromLogin && (emailFromParams || phoneFromParams)) {
      setVerificationStep("register");
      // Auto-send OTP immediately
      handleSendOTP(emailFromParams || "", phoneFromParams || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromParams, phoneFromParams, isFromLogin]);

  const handleSendOTP = async (email, phone) => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${BASE_URL}/users/send-otp`, {
        method: "POST",
        body: JSON.stringify({
          email: email,
          phone: phone,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store expiry time and start timer
        if (data.expiresAt) {
          setOtpExpiry(data.expiresAt);
        }

        // Format expiry time for display
        const expiryTime = data.expiresAt
          ? new Date(data.expiresAt).toLocaleTimeString()
          : "";
        const expiryMessage = expiryTime ? ` (Valid until ${expiryTime})` : "";

        toast.success(
          t("OTP_Sent_To") + " " + (email || phone) + expiryMessage,
          {
            autoClose: 5000,
            position: "bottom-right",
          }
        );

        // Start resend cooldown
        setResendCooldown(60);
        const cooldownInterval = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(cooldownInterval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error(data.error || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Error sending OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error(t("OTP_Required"));
      return;
    }

    setIsLoading(true);
    try {
      const response = await authFetch(`${BASE_URL}/users/verify-otp`, {
        method: "POST",
        body: JSON.stringify({
          otp: otpCode,
          email: userEmail,
          phone: userPhone,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t("OTP_Verified"), {
          autoClose: 3000,
          position: "bottom-right",
        });

        // If coming from login, show success message and redirect back to login
        if (isFromLogin) {
          toast.success("Phone verified successfully! You can now login.", {
            autoClose: 3000,
            position: "bottom-right",
          });
          setTimeout(() => {
            navigate("/auth/login?mode=login");
          }, 2000);
        } else {
          // If from registration, redirect to appropriate page
          setTimeout(() => {
            const userData = JSON.parse(localStorage.getItem("user") || "{}");
            if (userData.userType === "demand") {
              navigate("/");
              window.location.reload();
            } else if (userData.userType === "offer") {
              login(userData);
              navigate("/subscription");
            } else {
              navigate("/");
            }
          }, 1000);
        }
      } else {
        toast.error(data.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Error verifying OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = () => {
    if (resendCooldown > 0) return;
    handleSendOTP(userEmail, userPhone);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!userEmail && !userPhone) {
      toast.error("Please provide either email or phone number.");
      return;
    }
    await handleSendOTP(userEmail, userPhone);
    setVerificationStep("register");
    // let isFromLogin = "register";
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    if (!newPhoneNumber.trim()) {
      toast.error("Please enter a new phone number.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authFetch(`${BASE_URL}/users/update-phone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          newPhoneNumber: newPhoneNumber.trim(),
        }),
      });

      const data = await response.json().catch(() => ({})); // handle invalid JSON safely

      if (response.ok) {
        toast.success(data.message || "Phone number updated successfully!", {
          autoClose: 3000,
          position: "bottom-right",
        });

        // ✅ Update state after success
        setUserPhone(newPhoneNumber.trim());
        setShowUpdatePhone(false);
        setNewPhoneNumber("");

        // ✅ Trigger OTP for the new number
        try {
          await handleSendOTP(userEmail, newPhoneNumber.trim());
        } catch (error) {
          console.error("Error sending OTP to new phone number:", error);
          toast.error(error.error, { position: "bottom-right" });
        }
      } else {
        // ❌ Backend responded with an error (400, 404, 500, etc.)
        const errorMessage =
          // data?.message ||
          data?.error || `Request failed with status ${response.status}`;
        toast.error(errorMessage, { position: "bottom-right" });
        console.error("Update phone failed:", errorMessage, data);
      }
    } catch (error) {
      // ❌ Network or unexpected error
      console.error("Error updating phone number:", error);
      toast.error("Network error — please try again later.", {
        position: "bottom-right",
      });
    } finally {
      setIsLoading(false);
    }

    // try {
    //   const response = await authFetch(`${BASE_URL}/users/update-phone`, {
    //     method: "POST",
    //     body: JSON.stringify({
    //       newPhoneNumber: newPhoneNumber.trim(),
    //     }),
    //   });

    //   const data = await response.json();

    //   if (response.ok) {
    //     toast.success("Phone number updated successfully!", {
    //       autoClose: 3000,
    //       position: "bottom-right",
    //     });

    //     // Update the phone number and send new OTP
    //     setUserPhone(newPhoneNumber.trim());
    //     setShowUpdatePhone(false);
    //     setNewPhoneNumber("");

    //     // Send OTP to the new phone number
    //     try {
    //       const res = await handleSendOTP(userEmail, newPhoneNumber.trim());
    //     } catch (error) {
    //       console.error("Error sending OTP to new phone number:", error);
    //       toast.error(
    //         "Error sending OTP to new phone number. Please try again.",
    //         error
    //       );
    //     }
    //   } else {
    //     toast.error("Failed to update phone number. Please try again.");
    //   }
    // } catch (error) {
    //   console.error("Error updating phone number:", error);
    //   toast.error("Error updating phone number. Please try again.");
    // } finally {
    //   setIsLoading(false);
    // }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(5px)",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 15px 35px rgba(0, 0, 0, 0.1)",
          width: "100%",
          maxWidth: "400px",
          padding: "40px",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "30px" }}>
          <h2
            style={{
              color: "#333",
              marginBottom: "10px",
              fontSize: "28px",
              fontWeight: "600",
            }}
          >
            {isFromLogin ? t("Verify_Phone") : t("Verify_OTP")}
          </h2>
          <p
            style={{
              color: "#666",
              fontSize: "16px",
              lineHeight: "1.5",
            }}
          >
            {isFromLogin
              ? t("OTP_Contact_Message")
              : `${t("OTP_Sent_To")} ${userEmail || userPhone}`}
          </p>
        </div>

        {verificationStep !== "register" ? (
          <form onSubmit={handleContactSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#333",
                  fontWeight: "500",
                }}
              >
                {t("Email")}
              </label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder={t("Enter_Email")}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "2px solid #e1e5e9",
                  borderRadius: "6px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#29B185")}
                onBlur={(e) => (e.target.style.borderColor = "#e1e5e9")}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#333",
                  fontWeight: "500",
                }}
              >
                {t("Phone")}
              </label>
              <input
                type="tel"
                value={userPhone}
                onChange={(e) => setUserPhone(e.target.value)}
                placeholder={t("Enter_Phone")}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "2px solid #e1e5e9",
                  borderRadius: "6px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#29B185")}
                onBlur={(e) => (e.target.style.borderColor = "#e1e5e9")}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: "#29B185",
                color: "white",
                padding: "12px 20px",
                border: "none",
                borderRadius: "6px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "16px",
                fontWeight: "500",
                width: "100%",
                opacity: isLoading ? 0.7 : 1,
                transition: "opacity 0.3s ease",
              }}
            >
              {isLoading ? t("Sending") : t("Send_OTP")}
            </button>
          </form>
        ) : (
          <div>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#333",
                  fontWeight: "500",
                }}
              >
                {t("Enter_OTP")}
              </label>
              <input
                type="text"
                value={otpCode}
                onChange={handleOTPChange}
                placeholder="000000"
                maxLength="6"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "2px solid #e1e5e9",
                  borderRadius: "6px",
                  fontSize: "18px",
                  textAlign: "center",
                  letterSpacing: "2px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#29B185")}
                onBlur={(e) => (e.target.style.borderColor = "#e1e5e9")}
              />
            </div>

            {/* OTP Timer Display */}
            {timeRemaining > 0 && (
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "15px",
                  padding: "8px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  border: "1px solid #e1e5e9",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#666",
                    fontWeight: "500",
                  }}
                >
                  OTP expires in:{" "}
                  <span style={{ color: "#29B185", fontWeight: "600" }}>
                    {Math.floor(timeRemaining / 60)}:
                    {(timeRemaining % 60).toString().padStart(2, "0")}
                  </span>
                </p>
              </div>
            )}

            <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
              <button
                onClick={handleVerifyOTP}
                disabled={isLoading || otpCode.length !== 6}
                style={{
                  backgroundColor: "#29B185",
                  color: "white",
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "6px",
                  cursor:
                    isLoading || otpCode.length !== 6
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  flex: 1,
                  opacity: isLoading || otpCode.length !== 6 ? 0.7 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                {isLoading ? t("Verifying") : t("Verify_OTP")}
              </button>

              <button
                onClick={() => handleSendOTP(userEmail, userPhone)}
                disabled={isLoading || resendCooldown > 0}
                style={{
                  backgroundColor: "#f8f9fa",
                  color: resendCooldown > 0 ? "#999" : "#29B185",
                  padding: "12px 20px",
                  border: "2px solid #e1e5e9",
                  borderRadius: "6px",
                  cursor:
                    isLoading || resendCooldown > 0 ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  flex: 1,
                  opacity: isLoading || resendCooldown > 0 ? 0.7 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                {resendCooldown > 0
                  ? `${t("Send_OTP")} (${resendCooldown}s)`
                  : t("Send_OTP")}
              </button>
            </div>

            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  color: "#666",
                  fontSize: "14px",
                  marginBottom: "10px",
                }}
              >
                {t("OTP_Not_Received")}
              </p>
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendCooldown > 0 || isLoading}
                style={{
                  background: "none",
                  border: "none",
                  color: resendCooldown > 0 ? "#999" : "#29B185",
                  cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  textDecoration: "underline",
                  opacity: resendCooldown > 0 ? 0.7 : 1,
                }}
              >
                {resendCooldown > 0
                  ? `${t("Resend")} (${resendCooldown}s)`
                  : t("Resend")}
              </button>
            </div>

            {/* Update Phone Number Section */}
            <div
              style={{
                marginTop: "20px",
                padding: "15px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e1e5e9",
              }}
            >
              <p
                style={{
                  color: "#666",
                  fontSize: "14px",
                  marginBottom: "10px",
                  textAlign: "center",
                }}
              >
                Wrong phone number?
              </p>

              {!showUpdatePhone ? (
                <button
                  type="button"
                  onClick={() => setShowUpdatePhone(true)}
                  disabled={isLoading}
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#29B185",
                    padding: "8px 16px",
                    border: "2px solid #29B185",
                    borderRadius: "6px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    width: "100%",
                    opacity: isLoading ? 0.7 : 1,
                    transition: "all 0.3s ease",
                  }}
                >
                  Update Phone Number
                </button>
              ) : (
                <form onSubmit={handleUpdatePhone}>
                  <div style={{ marginBottom: "15px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        color: "#333",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      New Phone Number
                    </label>
                    <input
                      type="tel"
                      value={newPhoneNumber}
                      onChange={(e) => setNewPhoneNumber(e.target.value)}
                      placeholder="+1234567890"
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "2px solid #e1e5e9",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.3s ease",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#29B185")}
                      onBlur={(e) => (e.target.style.borderColor = "#e1e5e9")}
                    />
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      type="submit"
                      disabled={isLoading || !newPhoneNumber.trim()}
                      style={{
                        backgroundColor: "#29B185",
                        color: "white",
                        padding: "8px 16px",
                        border: "none",
                        borderRadius: "6px",
                        cursor:
                          isLoading || !newPhoneNumber.trim()
                            ? "not-allowed"
                            : "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        flex: 1,
                        opacity: isLoading || !newPhoneNumber.trim() ? 0.7 : 1,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      {isLoading ? "Updating..." : "Update & Send OTP"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowUpdatePhone(false);
                        setNewPhoneNumber("");
                      }}
                      disabled={isLoading}
                      style={{
                        backgroundColor: "#f8f9fa",
                        color: "#666",
                        padding: "8px 16px",
                        border: "2px solid #e1e5e9",
                        borderRadius: "6px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        flex: 1,
                        opacity: isLoading ? 0.7 : 1,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              color: "#666",
              cursor: "pointer",
              fontSize: "14px",
              textDecoration: "underline",
            }}
          >
            {t("Back_To_Login")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
